import { CoreEvent } from '@carvana/core-events';
import { datadogRum } from '@datadog/browser-rum';
import { useCallback, useEffect, useRef } from 'react';
import {
  type MfeModalPlaceholderCloseReason,
  showMfeModalPlaceholder
} from '@/common/Modals/MfeModalPlaceholder';
import {
  DEFAULT_MFE_MODAL_PLACEHOLDER_MESSAGE,
  DEFAULT_MFE_READY_FAILSAFE_MS,
  MFE_MODAL_DEFINITIONS,
  type MfeModalKey,
  type MfeModalPayload
} from '~/constants/common/MfeModules';
import useMfeReady from './useMfeReady';

// Keeps the placeholder up while the MFE modal mounts, so the backdrop never drops out in between.
const PLACEHOLDER_OVERLAP_MS = 150;

export type OpenMfeModalOptions = {
  // An extra gate ANDed with the MFE's ready state, e.g. a host mount gate.
  canOpen?: boolean;
  datadogContext?: Record<string, string>;
  isBypassed?: boolean;
  message?: string;
  onDismiss?: () => void;
  onTimeout?: () => void;
  // Set to false to hold the request without showing the placeholder modal.
  showPlaceholder?: boolean;
  timeoutMs?: number;
};

type PendingOpen<Key extends MfeModalKey> = {
  hide: (reason: MfeModalPlaceholderCloseReason) => void;
  payload: MfeModalPayload<Key>;
};

// Opens a modal MFE once it is ready, showing a placeholder modal while the request waits.
export default function useOpenMfeModal<Key extends MfeModalKey>(
  key: Key,
  options: OpenMfeModalOptions = {}
) {
  const { openEvent } = MFE_MODAL_DEFINITIONS[key];
  const isMfeReady = useMfeReady(key, { isBypassed: options.isBypassed });
  const isReady = (options.canOpen ?? true) && isMfeReady;
  const isReadyRef = useRef(isReady);
  const optionsRef = useRef(options);
  const pendingRef = useRef<PendingOpen<Key> | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  isReadyRef.current = isReady;
  optionsRef.current = options;

  const settle = useCallback((reason: MfeModalPlaceholderCloseReason) => {
    const pending = pendingRef.current;

    clearTimeout(timerRef.current);
    pendingRef.current = null;
    pending?.hide(reason);

    return pending;
  }, []);

  const cancel = useCallback(() => {
    settle('cancelled');
  }, [settle]);

  const open = useCallback(
    (payload: MfeModalPayload<Key>) => {
      settle('cancelled');

      if (isReadyRef.current) {
        CoreEvent.emit(openEvent, payload);

        return;
      }

      const { datadogContext, message, showPlaceholder, timeoutMs } =
        optionsRef.current;
      const waitMs = timeoutMs ?? DEFAULT_MFE_READY_FAILSAFE_MS;
      const pending: PendingOpen<Key> = {
        hide:
          showPlaceholder === false
            ? () => {}
            : showMfeModalPlaceholder({
                datadogContext: { ...datadogContext, mfe: key },
                message: message ?? DEFAULT_MFE_MODAL_PLACEHOLDER_MESSAGE,
                onDismiss: () => {
                  clearTimeout(timerRef.current);
                  pendingRef.current = null;
                  optionsRef.current.onDismiss?.();
                }
              }),
        payload
      };

      pendingRef.current = pending;
      timerRef.current = setTimeout(() => {
        if (pendingRef.current !== pending) return;

        datadogRum.addError(
          new Error(`${key} MFE failed to signal ready within ${waitMs}ms`),
          { ...datadogContext, location: 'useOpenMfeModal', mfe: key }
        );
        settle('timed-out');
        optionsRef.current.onTimeout?.();
      }, waitMs);
    },
    [key, openEvent, settle]
  );

  useEffect(() => {
    if (!isReady) return;

    const pending = pendingRef.current;

    if (!pending) return;

    clearTimeout(timerRef.current);
    pendingRef.current = null;
    CoreEvent.emit(openEvent, pending.payload);
    setTimeout(() => pending.hide('mfe-opened'), PLACEHOLDER_OVERLAP_MS);
  }, [isReady, openEvent]);

  useEffect(() => cancel, [cancel]);

  return { cancel, isReady, open };
}
